import { useQueries, useQuery } from '@tanstack/react-query';

import { useConnectorPresence } from '@/integration/hooks/useConnections.js';
import {
  oneDriveAccountQueryOptions,
  oneDriveScopeBudgetQueryOptions,
} from '@/lib/api/onedrive.js';
import { sharePointPlacesQueryOptions } from '@/lib/api/sharepoint.js';

import { useNothingSelectedDismissed } from './nothing-selected-dismissal.js';

/**
 * How much of a Microsoft connection is actually exposed to a workspace, as one number.
 *
 * SharePoint places and OneDrive folders are two budgets with two ceilings, but they hang off the
 * SAME connection — `onedrive_accounts` is keyed by the SharePoint connection id — so from the
 * outside they are one question: how much of this connection can the agent reach here.
 *
 * The COUNTS add up; the ceilings do not, and used to. Twenty-five places and ten folders are
 * separate budgets, so their sum is a number nobody can reach: "4 of 35" promised room for
 * thirty-one more, and somebody sitting at twenty-five places and no folders read "25 of 35" and
 * went looking for space on a list that was full. Each ceiling is stated over the list it binds,
 * inside the dialog, and never out here where the two are one number.
 */
export interface SelectedFolderCount {
  /** Places and folders together, in this workspace, on this connection. */
  selected: number;
  /**
   * Whether the number can be shown at all. A read that failed contributes nothing, and a count
   * missing one of its halves reads as "fewer are exposed than really are" — which is the wrong way
   * for a number that decides whether a connection looks unconfigured.
   */
  ready: boolean;
  /**
   * Whether this caller may change the list. Only a connection's owner can, and a mark urging
   * somebody to configure something they cannot configure is noise they can never clear.
   */
  canConfigure: boolean;
  /**
   * Whether the SharePoint half is limited to its list at all. Every connection starts restricted,
   * but the policy can still be turned off, and then it reaches the whole account — so its count
   * would say what has been collected, not what the agent reaches.
   */
  restricted: boolean;
  /**
   * Whether the pair holds the whole-drive OneDrive grant, which only an open pair in its owner's
   * personal workspace does. Anywhere else an open pair's OneDrive is still its listed folders.
   * Null until the scopes are read.
   */
  wholeOneDrive: boolean | null;
}

/**
 * The count for one connection in one workspace.
 *
 * OneDrive folders are filtered by connection because a person's OneDrive is bound to exactly one
 * SharePoint connection, while a workspace can reach several — counting all of them would credit
 * every card with the same folders.
 */
export function useSelectedFolderCount(
  workspaceId: string,
  connectionId: string,
): SelectedFolderCount {
  const placesQuery = useQuery(
    sharePointPlacesQueryOptions(workspaceId, connectionId),
  );
  const foldersQuery = useQuery(oneDriveScopeBudgetQueryOptions(workspaceId));
  const accountQuery = useQuery(
    oneDriveAccountQueryOptions(workspaceId, connectionId),
  );
  const scopes = (foldersQuery.data?.scopes ?? []).filter(
    (scope) => scope.connectionId === connectionId,
  );
  /** The open grant is the pair's reach, not something somebody selected. */
  const folders = scopes.filter((scope) => !scope.openGrant);
  return {
    selected: (placesQuery.data?.places.length ?? 0) + folders.length,
    ready:
      placesQuery.isSuccess && foldersQuery.isSuccess && accountQuery.isSuccess,
    canConfigure: placesQuery.data?.isOwner === true,
    restricted: placesQuery.data?.enforced !== false,
    wholeOneDrive: foldersQuery.isSuccess
      ? scopes.some((scope) => scope.openGrant)
      : null,
  };
}

/**
 * Whether a count is the one the red mark is about: a restricted connection, reaching nothing, seen
 * by the owner who could change that. A count that has not answered is never this.
 */
export function ownerReachesNothing(count: SelectedFolderCount): boolean {
  return (
    count.ready &&
    count.restricted &&
    count.canConfigure &&
    count.selected === 0
  );
}

/**
 * Whether this workspace holds a SharePoint connection that exposes nothing, and that THIS caller
 * could do something about.
 *
 * It answers the marks further out — the Connectors tab, and the settings control that leads to it —
 * so all of them agree with the card they eventually lead to. A connection somebody else owns is
 * left out on purpose: a mark you cannot clear is a mark you learn to ignore, and then it fails the
 * one time it means something.
 *
 * Silence on failure, for the same reason the count itself is: a read that did not answer must not
 * be reported as "nothing is exposed". Silence too where the owner already closed the list here
 * with nothing on it: that was the answer, see `nothing-selected-dismissal.ts`.
 */
export function useSharePointNeedsFolders(workspaceId: string | null): boolean {
  const isDismissed = useNothingSelectedDismissed();
  const presence = useConnectorPresence(workspaceId, workspaceId !== null);
  const connections = presence.data?.providers?.sharepoint?.connections ?? [];
  const policies = useQueries({
    queries: connections.map((connection) =>
      sharePointPlacesQueryOptions(workspaceId ?? '', connection.connectionId),
    ),
  });
  const folders = useQuery({
    ...oneDriveScopeBudgetQueryOptions(workspaceId ?? ''),
    enabled: workspaceId !== null && connections.length > 0,
  });
  if (connections.length === 0 || !folders.isSuccess) return false;
  return connections.some((connection, index) => {
    const policy = policies[index];
    if (!policy?.isSuccess || policy.data.isOwner !== true) return false;
    if (isDismissed(workspaceId ?? '', connection.connectionId)) return false;
    /** Not restricted means it reaches everything — the opposite of exposing nothing. */
    if (!policy.data.enforced) return false;
    const ownFolders = folders.data.scopes.filter(
      (scope) => scope.connectionId === connection.connectionId,
    );
    return policy.data.places.length + ownFolders.length === 0;
  });
}

/**
 * What an open pair reaches. OneDrive is left out only once the pair is known to hold no whole-drive
 * grant: a person's OneDrive hangs off one connection, so another open connection reaches SharePoint
 * alone. While the grant is unknown both are named, so the reach is never understated.
 */
export function reachName(wholeOneDrive: boolean | null): string {
  return wholeOneDrive === false ? 'SharePoint' : 'SharePoint and OneDrive';
}
